// Modal overlay showing full card detail. Closes on Esc or backdrop click.

import { useRef } from "react";
import type { ProcedureCard, AttackCard, Stage } from "../types";
import { CardArt } from "./CardArt";
import { useDialog } from "./useDialog";

const STAGE_LABEL: Record<Stage, string> = {
  initialCompromise: "Initial Compromise",
  pivotEscalate: "Pivot / Escalate",
  c2Exfil: "C2 / Exfil",
  persistence: "Persistence",
};

interface Props {
  card: ProcedureCard | AttackCard | null;
  kind: "procedure" | "stage" | "inject";
  onClose: () => void;
}

export function CardDetail({ card, kind, onClose }: Props) {
  const dialogRef = useRef<HTMLDivElement>(null);
  useDialog(dialogRef, onClose);
  if (!card) return null;

  const isProc = "detects" in card;
  return (
    <div className="modal-backdrop" onClick={onClose}>
      <div
        className="modal"
        ref={dialogRef}
        role="dialog"
        aria-modal="true"
        aria-label={`${card.name} — card detail`}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="modal-art">
          {card.imageUrl ? (
            <img src={card.imageUrl} alt="" />
          ) : (
            <CardArt cardId={card.id} kind={kind} height={260} />
          )}
        </div>
        <div className="modal-body">
          <div
            style={{
              fontFamily: "var(--font-mono)",
              fontSize: 11,
              letterSpacing: "0.18em",
              color: "var(--color-text-subtle)",
              textTransform: "uppercase",
              marginBottom: 6,
            }}
          >
            {kind === "procedure" ? "Procedure" : kind === "stage" ? STAGE_LABEL[(card as AttackCard).stage] : "Inject"}
          </div>
          <h2 style={{ margin: "0 0 10px", fontSize: 22, letterSpacing: "-0.02em" }}>{card.name}</h2>
          {isProc ? (
            <>
              <p style={{ fontSize: 13, lineHeight: 1.55 }}>{(card as ProcedureCard).description}</p>
              {(card as ProcedureCard).d3fend && (
                <div
                  style={{
                    marginTop: 14,
                    fontFamily: "var(--font-mono)",
                    fontSize: 11,
                    color: "var(--color-text-muted)",
                  }}
                >
                  <span
                    style={{
                      color: "var(--color-accent)",
                      marginRight: 8,
                      letterSpacing: "0.16em",
                      textTransform: "uppercase",
                      fontSize: 10,
                    }}
                  >
                    D3FEND
                  </span>
                  {(card as ProcedureCard).d3fend}
                </div>
              )}
              {(card as ProcedureCard).detects.length > 0 && (
                <>
                  <h3 style={{ marginTop: 18 }}>Can detect</h3>
                  <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
                    {(card as ProcedureCard).detects.map((d) => (
                      <span
                        key={d}
                        style={{
                          fontFamily: "var(--font-mono)",
                          fontSize: 11,
                          padding: "3px 8px",
                          border: "1px solid var(--color-border)",
                          color: "var(--color-text-muted)",
                        }}
                      >
                        {d}
                      </span>
                    ))}
                  </div>
                </>
              )}
            </>
          ) : (
            <>
              <p style={{ fontSize: 13, lineHeight: 1.6, fontStyle: "italic", color: "var(--color-text-muted)" }}>
                {(card as AttackCard).flavor}
              </p>
              {(card as AttackCard).attck && (
                <div
                  style={{
                    marginTop: 14,
                    fontFamily: "var(--font-mono)",
                    fontSize: 11,
                    color: "var(--color-text-muted)",
                    letterSpacing: "0.04em",
                  }}
                >
                  <span style={{ color: "var(--color-accent)", marginRight: 8, letterSpacing: "0.16em", textTransform: "uppercase", fontSize: 10 }}>
                    ATT&CK
                  </span>
                  {(card as AttackCard).attck}
                </div>
              )}
            </>
          )}
          <div style={{ marginTop: 22, display: "flex", justifyContent: "flex-end" }}>
            <button onClick={onClose}>Close — Esc</button>
          </div>
        </div>
      </div>
    </div>
  );
}
